import { fabric } from 'fabric';
import type { Constructor } from '../types';
import { generateJitteredKeyBetween } from 'fractional-indexing-jittered';
import {
  hasFrameMemberBehaviors,
  isFrameLike,
  isSimpleGroup,
  isSimpleSelection,
  hasLayout,
} from './guards';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import {
  calculateFrameLayout,
  type FrameLayoutInfo,
} from './LayoutCalculation';
import type FrameMemberBehaviors from './FrameMemberBehaviors';
import ResizeControl from '../controls/ResizeControl';
import type FrameBehaviors from './FrameBehaviors';
import ShapeLinearResizeControl from '../controls/ShapeLinearResizeControl';
import type ShapeResizeControl from '../controls/ShapeResizeControl';
import AddObjectControl from '../controls/AddObjectControl';
import { sum } from 'lodash';
import ToolDelete from '../tools/ToolDelete';

interface LayoutBehaviors {
  proximityBorder: boolean;
  isCursorInProximity: boolean;

  layoutEnabled: boolean;
  // For use when running functions that layout might interfere with.
  layoutSizeHorizontal: 'children' | 'parent' | 'user';
  layoutSizeVertical: 'children' | 'parent' | 'user';
  layoutGap: number | 'auto';
  layoutPadding: number;
  layoutDirection: 'horizontal' | 'vertical' | 'wrap';
  layoutAlignHorizontal: 'left' | 'center' | 'right';
  layoutAlignVertical: 'top' | 'center' | 'bottom';

  initializeLayout(): void;
  positionMember(object: fabric.Object, x: number, y: number): void;
  layout(updateAll?: boolean): void;
  layoutWithChildren(): void;
  cursorInProximity(): boolean;
  shouldRenderLayoutBorder(): boolean;

  // Methods for setting object for which we will render an indicator
  setIndicatorObject(object: fabric.Object): void;
  clearIndicatorObject(): void;
  resizeUsingWidth(): boolean;
  resizeUsingHeight(): boolean;

  // Helper methods
  isHorizontal(): boolean;
  fillsParent(): boolean;
  fillsParentHorizontally(): boolean;
  fillsParentVertically(): boolean;
  sortedChildren(): (fabric.Object & FrameMemberBehaviors)[];

  showAutoGapButton(): boolean;
}

interface Layoutable {
  layoutMarginSides?(): SpacingSidesStruct;
}

interface Position {
  x: number;
  y: number;
}
interface SizeStruct {
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
}

// Behaviors that can be added to objects to automatically layout the members of
// the frame.
//
// Handling of groups here is particularly tricky. We treat groups as a single
// unit for layout and only the group itself is laid out. However for selections
// (which are a subclass of group) we lay out the children. This is because
// the selection goes away and we need the children to maintain their position
// in the layout.
//
// eslint-disable-next-line @typescript-eslint/no-redeclare
function LayoutBehaviors(Base: Constructor<fabric.Object>): any {
  const WithLayoutBehaviors = class
    extends (Base as Constructor<
      FrameBehaviors & Layoutable & FrameMemberBehaviors & fabric.Object
    >)
    implements LayoutBehaviors
  {
    // Whether to show the border only when the cursor is in proximity.
    proximityBorder = true;

    // Last status of cursor proximity.
    isCursorInProximity: boolean;

    // Keys that will be serialized for saving and sending to remote systems.
    serializedKeys = [
      // @ts-expect-error serializedKeys is declared on subclasses
      ...this.serializedKeys,
      'layoutEnabled',
      'layoutSizeHorizontal',
      'layoutSizeVertical',
      'layoutGap',
      'layoutPadding',
      'layoutDirection',
      'layoutAlignHorizontal',
      'layoutAlignVertical',
      'layoutMinWidth',
      'layoutMinHeight',
      'layoutGapBefore',
      'layoutGapAfter',
    ];

    // Layout properties.
    layoutEnabled: boolean;
    layoutPosition: string; // Order of this child in a parent layout using fractional indexing.
    layoutSizeHorizontal: 'children' | 'parent' | 'user'; // Whether the layout should grow to fit its children, fill its parent, or use a user defined size.
    layoutSizeVertical: 'children' | 'parent' | 'user';
    layoutMinWidth?: number;
    layoutMinHeight?: number;
    layoutGap: number | 'auto'; // Space betweeen item or 'auto' to automatically space items and ignore alignment.
    layoutPadding: number; // Padding inside the layout.
    layoutDirection: 'horizontal' | 'vertical' | 'wrap';
    layoutAlignHorizontal: 'left' | 'center' | 'right';
    layoutAlignVertical: 'top' | 'center' | 'bottom';

    // Whether layout is currently being done, so we don't reenter and layout in an infinite loop.
    doingLayout = false;

    // Object being added/moved around layout for which we will render an indicator
    indicatorObject: (FrameMemberBehaviors & fabric.Object) | null;

    initializeLayout() {
      this.updateEnabled();
      this.updateMinDimensions();

      if (this.layoutEnabled) {
        this.setLayoutDefaults();
      }

      // Detect when the cursor is close to the layout so we can show the border.
      const proximityDetection = () => {
        if (this.proximityBorder) {
          const newProximity = this.cursorInProximity();

          if (newProximity !== this.isCursorInProximity) {
            this.isCursorInProximity = newProximity;
            this.canvas.requestRenderAll();
          }
        }
      };
      this.on('added', () => this.canvas.on('mouse:move', proximityDetection));
      this.on('removed', () =>
        this.canvas.off('mouse:move', proximityDetection)
      );
      this.on('before:control:action', e => {
        if (e.control) {
          this.updateLayoutToManual(e.control);
        }
      });

      this.on('selected', () => {
        this.updateAddControl();
      });

      this.on('mouseup', e => {
        if (
          !this.layoutEnabled ||
          this.canvas.mouseHasMoved ||
          this.canvas.getActiveObject() !== this
        ) {
          return;
        }

        const pointer = this.canvas.getPointer(e.e, true);
        // Casting here as we only care about coordinates
        const target = this.findTopmostChildAtPoint(
          new fabric.Point(pointer.x, pointer.y)
        );

        if (target) {
          this.canvas.setActiveObject(target);
          this.canvas.requestRenderAll();
        }
      });

      // When layout is moved or resized, update layout and child layouts
      ['moved', 'scaled', 'scaling'].forEach(eventName =>
        this.on(eventName, () => this.layoutWithChildren())
      );
    }

    static createAddObjectControl() {
      return new AddObjectControl();
    }

    static addControl = this.createAddObjectControl();

    setLayoutDefaults() {
      // Make sure our defaults are available (but not saved).
      const setDefault = (key, value) => {
        if (this[key] === undefined || this[key] === null) this[key] = value;
      };
      setDefault('layoutGap', 10);
      setDefault('layoutPadding', 10);
      setDefault('layoutDirection', 'vertical');
      setDefault('layoutAlignHorizontal', 'center');
      setDefault('layoutAlignVertical', 'top');
      setDefault('layoutSizeHorizontal', 'user');
      setDefault('layoutSizeVertical', 'user');
    }

    deleted() {
      ToolDelete.deleteObjects(this.children, this.controller);

      super.deleted(this.controller);
    }

    minDimension() {
      return 20;
    }

    maxDimension() {
      return Number.MAX_SAFE_INTEGER;
    }

    includeObjectInLayout(object: fabric.Object): boolean {
      return (
        object.type !== 'connector' && // Don't lay out connectors.
        // !object.simpleGroup && // Group members can't be laid out. Only the group.
        hasFrameMemberBehaviors(object) &&
        !object.layoutManual // Individual objects can opt out of layout.
      );
    }

    updateEnabled() {
      if (this.layoutEnabled) {
        // Make sure that all members have a valid position. We re-sort based
        // on the position of the items.
        const children: (fabric.Object & FrameMemberBehaviors)[] =
          this.sortedChildren()
            .map(child => {
              child.layoutPosition = null;
              return child;
            })
            .sort((a: fabric.Object, b: fabric.Object) =>
              this.layoutDirection === 'vertical'
                ? (a?.top || Number.MIN_SAFE_INTEGER) -
                  (b?.top || Number.MIN_SAFE_INTEGER)
                : (a?.left || Number.MIN_SAFE_INTEGER) -
                  (b?.left || Number.MIN_SAFE_INTEGER)
            );
        for (let i = 0; i < children.length; i++) {
          const afterItem: (fabric.Object & FrameMemberBehaviors) | null =
            children[i - 1];
          const beforeItem: (fabric.Object & FrameMemberBehaviors) | null =
            children[i + 1];

          if (
            afterItem?.layoutPosition &&
            beforeItem?.layoutPosition &&
            afterItem?.layoutPosition === beforeItem?.layoutPosition
          ) {
            // This should not happen and would crash in generateKeyBetween so
            // just log it.
            console.warn(
              `Can't layout children with the same position ${afterItem?.layoutPosition}.`
            );
          } else {
            const newPosition = generateJitteredKeyBetween(
              afterItem?.layoutPosition ?? null,
              beforeItem?.layoutPosition ?? null
            );
            children[i]?.set('layoutPosition', newPosition);
          }
        }
      }
    }

    _render(ctx: CanvasRenderingContext2D) {
      super._render(ctx);

      this.renderIndicator(ctx);
    }

    private renderIndicator(ctx: CanvasRenderingContext2D) {
      if (!this.layoutEnabled || !this.indicatorObject) {
        return;
      }

      const children = this.sortedChildren();
      const object = this.indicatorObject;
      const index = children.findIndex(
        obj => obj.layoutPosition === object.layoutPosition
      );
      const beforeObject = children[index + 1];
      const afterObject = children[index - 1];
      ctx.save();

      // Keep indicator the same size regardless of frame scale
      ctx.scale(1 / this.scaleX, 1 / this.scaleY);

      // Calculate the indicator position
      const padding = this.layoutPaddingSides();
      const isHorizontal = this.isHorizontal();
      let zoom = this.canvas?.getZoom() ?? 1;
      if (zoom < 1) zoom = (zoom + 1) / 2;
      const indicatorSize = 5 / zoom;

      // Set up styling for the indicator
      ctx.strokeStyle = '#4597F8';
      ctx.fillStyle = '#4597F8';
      ctx.lineWidth = indicatorSize;
      ctx.lineCap = 'round';

      const x0 = -this.getScaledWidth() / 2;
      const y0 = -this.getScaledHeight() / 2;
      // Calculate layout bounds
      const layoutRight = x0 + this.getScaledWidth();
      const layoutBottom = y0 + this.getScaledHeight();
      let x1, y1, x2, y2;

      const { leftOffset, topOffset } = this.getChildOffset(
        calculateFrameLayout(this),
        object
      );

      // Calculate the gap to use for indicator positioning
      const getIndicatorGap = (after, before) => {
        // Check if any children have individual gap settings
        const hasIndividualGaps = children.some(
          child => child.layoutGapBefore != null || child.layoutGapAfter != null
        );

        if (hasIndividualGaps) {
          // Use individual gaps with additive behavior
          const gapAfter = after?.layoutGapAfter ?? 0;
          const gapBefore = before?.layoutGapBefore ?? 0;
          return gapAfter + gapBefore;
        } else {
          // Fall back to global gap behavior
          return this.layoutGap === 'auto' ? 10 : this.layoutGap || 0;
        }
      };

      if (isHorizontal) {
        const indicatorGap = getIndicatorGap(afterObject, beforeObject);
        const indicatorX = afterObject
          ? afterObject.left -
            this.left +
            afterObject.getScaledWidth() +
            indicatorGap / 2
          : beforeObject
          ? beforeObject.left - this.left - indicatorGap / 2
          : padding.left;

        // Constrain indicatorX within layout bounds
        const clampedX = Math.max(
          x0 + padding.left - indicatorSize / 2,
          Math.min(layoutRight - padding.right, x0 + indicatorX)
        );

        x1 = x2 = clampedX;
        y1 = y0 + topOffset + padding.top;
        y2 = Math.min(
          layoutBottom - padding.bottom,
          y1 + object.getScaledHeight()
        );
        y1 = Math.max(y0 + padding.top, y1);
      } else {
        const indicatorGap = getIndicatorGap(afterObject, beforeObject);
        const indicatorY = afterObject
          ? afterObject.top -
            this.top +
            afterObject.getScaledHeight() +
            indicatorGap / 2
          : beforeObject
          ? beforeObject.top - this.top - indicatorGap / 2
          : padding.top;

        // Constrain indicatorY within layout bounds
        const clampedY = Math.max(
          y0 + padding.top - indicatorSize / 2,
          Math.min(layoutBottom - padding.bottom, y0 + indicatorY)
        );

        x1 = x0 + leftOffset + padding.left;
        x2 = Math.min(
          layoutRight - padding.right,
          x1 + object.getScaledWidth()
        );
        x1 = Math.max(x0 + padding.left, x1);
        y1 = y2 = clampedY;
      }

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Draw the indicator line with rounded ends
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      ctx.restore();
    }

    _set(keyOrPath: KeyPath, value: any): fabric.Object {
      const prevValue = this.get(keyOrPath as keyof this);

      /* Ignore tiny changes when doing layout, as this can result
      in an infinite loop of deferred operations as a tiny change
      to one property causes another property to change by a tiny amount, causing
      the first to in turn change again. The layout has
      completed from a visual standpoint so we can safely stop. */
      if (
        this.doingLayout &&
        typeof prevValue === 'number' &&
        typeof value === 'number' &&
        prevValue.toPrecision(5) === value.toPrecision(5)
      ) {
        return this;
      }
      super._set(keyOrPath, value);

      switch (keyOrPath) {
        case 'layoutEnabled':
          this.initializeLayout();
          this.updateAddControl();
          this.layout();
          // We need to render in case layout is disabled.
          this.canvas?.requestRenderAll();
          break;

        case 'layoutSizeHorizontal':
        case 'layoutSizeVertical':
          this.updateParentSize();
          this.updateChildrenSize();
          this.handleLayoutSizeChange(prevValue, value);
          break;

        case 'layoutGap':
        case 'layoutPadding':
        case 'layoutDirection':
        case 'layoutAlignHorizontal':
        case 'layoutAlignVertical':
          this.updateAddControl();
          this.updateAutoGap();
          this.layout();
          break;

        case 'width':
        case 'height':
          // Also layout our children when we change size.
          this.layoutWithChildren();
          break;
      }

      return this;
    }

    sortedChildren(): (fabric.Object & FrameMemberBehaviors)[] {
      return this.children
        .filter(
          (a): a is fabric.Object & FrameMemberBehaviors =>
            this.includeObjectInLayout(a) && hasFrameMemberBehaviors(a)
        )
        .filter(
          a =>
            !a.simpleGroup ||
            (a.simpleGroup && isSimpleSelection(a.simpleGroup)) // Only include grouped objects inside selections in the layout
        )
        .sort((a, b) => {
          // Note we need to do a case-sensitive comparison here for the
          // fractional indexing to work correctly.
          return (a.layoutPosition || '') < (b.layoutPosition || '') ? -1 : 1;
        });
    }

    addMember(object: fabric.Object) {
      if (!hasFrameMemberBehaviors(object)) return;

      if (!object.objectIsMoving()) {
        // Append to children. We append rather than prepend to avoid a problem
        // where moving from the default position of 0 doesn't work properly.
        const children = this.sortedChildren();
        const newPosition = generateJitteredKeyBetween(
          children[children.length - 1]?.layoutPosition ?? null,
          null
        );

        super.addMember(object);

        object.set({ layoutPosition: newPosition });

        this.layout();
      } else {
        super.addMember(object);
      }
    }

    removeMember(object: fabric.Object) {
      super.removeMember(object);
      object.set('layoutPosition', null);

      this.layout();
    }

    // Set the `layoutPositon` for a child as it is being dragged with a cursor at x, y.
    // This only sets the order of the child in the list of children, it does not
    // move any objects.
    positionMember(
      object: fabric.Object & FrameMemberBehaviors,
      x: number,
      y: number
    ) {
      if (!this.includeObjectInLayout(object)) return;
      if (!this.layoutEnabled) return;

      // Functions to use depending on the layout direction.
      let originProperty: 'left' | 'top';
      let dimensionFunction: 'getScaledWidth' | 'getScaledHeight';
      let position: number;

      if (this.layoutDirection === 'horizontal') {
        position = x;
        originProperty = 'left';
        dimensionFunction = 'getScaledWidth';
      } else {
        position = y;
        originProperty = 'top';
        dimensionFunction = 'getScaledHeight';
      }

      const children = this.sortedChildren();
      const selectedObjects = this.canvas.getActiveObjects();

      // If any children have null layoutPosition, initialize them
      const hasNullPositions = children.some(
        child => child.layoutPosition == null
      );
      if (hasNullPositions) {
        this.updateEnabled();
      }

      // Find the object that is being dragged over.
      let overChild = children.find(
        child =>
          object.id !== child.id &&
          !selectedObjects.includes(child) &&
          position >= child[originProperty] &&
          position <= child[originProperty] + child[dimensionFunction]()
      );

      // If not directly over a child, find the nearest child to determine positioning
      if (!overChild) {
        const eligibleChildren = children.filter(
          child => object.id !== child.id && !selectedObjects.includes(child)
        );

        if (eligibleChildren.length === 0) {
          return;
        }

        overChild = eligibleChildren.reduce((nearest, child) => {
          const childCenter =
            child[originProperty] + child[dimensionFunction]() / 2;
          const nearestCenter =
            nearest[originProperty] + nearest[dimensionFunction]() / 2;
          return Math.abs(position - childCenter) <
            Math.abs(position - nearestCenter)
            ? child
            : nearest;
        }, eligibleChildren[0]);
      }

      let newIndex: number = -1;
      const oldIndex = children.indexOf(object);

      if (overChild) {
        // See which child the cursor is over.
        const overIndex = children.indexOf(overChild);

        // Once we are 1/3 over another object then move it. Using 1/3 makes the
        // UI feel more responsive.
        const reorderThreshold = object.currentLayoutContainer
          ? overChild[dimensionFunction]() / 3
          : overChild[dimensionFunction]() / 2;
        if (oldIndex < overIndex) {
          newIndex =
            position < overChild[originProperty] + reorderThreshold
              ? overIndex
              : overIndex + 1;
        } else {
          newIndex =
            position <
            overChild[originProperty] +
              overChild[dimensionFunction]() -
              reorderThreshold
              ? overIndex
              : overIndex + 1;
        }
      }
      if (newIndex !== oldIndex) {
        const beforeItem = children[newIndex];
        const afterItem = children[newIndex - 1];

        const newPosition = generateJitteredKeyBetween(
          afterItem?.layoutPosition ?? null,
          beforeItem?.layoutPosition ?? null
        );
        object.set({ layoutPosition: newPosition });
      }
    }

    // Compute the size of this object.
    // Returns null if we expect it to be computed by the children.
    layoutSize(): [number | null, number | null] {
      let width, height;

      switch (this.layoutSizeHorizontal) {
        case 'children':
          width = null;
          break;
        case 'parent': {
          const parent = this.inFrame();
          if (parent) {
            width = this.getScaledWidth();
            break;
          } else {
            // If there is no parent then switch back to user defined size.
            this.set({ layoutSizeHorizontal: 'user' });
          }
        }

        // If we are not in a parent, fall back to user defined size.
        // eslint-disable-next-line no-fallthrough
        case 'user':
        default:
          width = this.getScaledWidth();
          break;
      }
      switch (this.layoutSizeVertical) {
        case 'children':
          height = null;
          break;
        case 'parent': {
          const parent = this.inFrame();
          if (parent) {
            height = this.getScaledHeight();
            break;
          } else {
            // If there is no parent then switch back to manual sizing.
            this.set({ layoutSizeVertical: 'user' });
          }
        }

        // If we are not in a parent, fall back to user defined size.
        // eslint-disable-next-line no-fallthrough
        case 'user':
        default:
          height = this.getScaledHeight();
          break;
      }

      return [width, height];
    }

    // Layout the children of this object. `updateAll` should be true if even the
    // moving children should be updated.
    layout(updateAll = false) {
      if (!this.layoutEnabled) return;
      if (this.doingLayout) return;

      this.controller.performBulkSensitiveCode(() => {
        try {
          // Don't reenter the layout algorithm.
          this.doingLayout = true;
          this.layoutInternal(updateAll);
        } finally {
          this.doingLayout = false;
        }
      });
    }

    layoutInternal(updateAll) {
      // Because of performBulkSensitiveCode, this code can run out of band. As
      // such, we need to ensure that layout is still enabled (e.g., if layoutEnabled
      // is undone)
      if (!this.layoutEnabled) return;

      const children = this.sortedChildren().filter(
        obj => !obj.objectIsMoving() || obj.currentLayoutContainer
      );
      let layout = calculateFrameLayout(this);

      // First, size any "fill parent" child frames
      const needsLayoutUpdate = this.sizeChildrenToFillParent(
        this.childFrames.filter(hasLayout),
        layout
      );

      // Recalculate layout if any child frames' dimensions changed
      if (needsLayoutUpdate) {
        layout = calculateFrameLayout(this);
      }

      if (this.fitsToChildren()) {
        // Calculate dimensions without positioning children.
        const { position, maxCrossAxisChild } = this.calculateChildDimensions(
          children,
          layout
        );

        // Update frame dimensions based on calculations
        this.sizeToFitChildren(position, maxCrossAxisChild, layout);
      }

      // Position children using the updated frame dimensions
      this.positionChildrenInLayout(children, layout, updateAll);

      this.canvas?.requestRenderAll();
    }

    private sizeChildrenToFillParent(
      childFrames: (LayoutBehaviors & fabric.Object)[],
      layout
    ): boolean {
      const isHorizontal = this.isHorizontal();
      const { effectiveWidth, effectiveHeight, total } = layout;
      let needsLayoutUpdate = false;
      // Child frames that have fill parent set
      const fillsParentChildren = isHorizontal
        ? childFrames.filter(child => child.fillsParentHorizontally())
        : childFrames.filter(child => child.fillsParentVertically());

      // Total width or height these childFrames are currently consuming
      const childrenSize = sum(
        fillsParentChildren.map(child =>
          isHorizontal ? child.getScaledWidth() : child.getScaledHeight()
        )
      );

      // Space used by padding, gap, and all non-fills-parent children
      const usedSpace =
        (isHorizontal ? total.width : total.height) - childrenSize;

      // Space available for each fills-parent child
      const availableWidth = isHorizontal
        ? (effectiveWidth - usedSpace) / fillsParentChildren.length
        : effectiveWidth;
      const availableHeight = !isHorizontal
        ? (effectiveHeight - usedSpace) / fillsParentChildren.length
        : effectiveHeight;

      childFrames.forEach(child => {
        const newSize: SizeStruct = {};
        const adjustWidth = child.resizeUsingWidth();
        const adjustHeight = child.resizeUsingHeight();

        // Handle horizontal sizing
        if (child.fillsParentHorizontally()) {
          if (adjustWidth) {
            newSize.width = availableWidth / child.scaleX;
          } else {
            newSize.scaleX = availableWidth / child.width;
          }
        }

        // Handle vertical sizing
        if (child.fillsParentVertically()) {
          if (adjustHeight) {
            newSize.height = availableHeight / child.scaleY;
          } else {
            newSize.scaleY = availableHeight / child.height;
          }
        }

        // Apply size changes if needed
        if (Object.keys(newSize).length > 0) {
          needsLayoutUpdate = Object.entries(newSize).some(
            ([key, value]) => this[key] !== value
          );
          child.set(newSize);
          child.setCoords();
        }
      });

      return needsLayoutUpdate;
    }

    private calculateChildDimensions(
      children: fabric.Object[],
      layout: FrameLayoutInfo
    ) {
      const position: Position = {
        x:
          this.left +
          layout.padding.left +
          (this.isHorizontal() ? layout.edgeGapBefore : 0),
        y:
          this.top +
          layout.padding.top +
          (!this.isHorizontal() ? layout.edgeGapBefore : 0),
      };
      let maxCrossAxisChild = 0;
      let addedChild = false;

      // Simulate positioning to calculate final dimensions
      children.forEach((child, index) => {
        const childSize = {
          width: child.getScaledWidth(),
          height: child.getScaledHeight(),
        };

        if (addedChild) {
          // Use individual gap for this position (index-1 because we're adding gap before current child)
          const gapToUse = layout.individualGaps[index - 1] ?? layout.gap ?? 0;
          if (this.isHorizontal()) {
            position.x += gapToUse;
          } else {
            position.y += gapToUse;
          }
        }

        addedChild = true;
        if (this.isHorizontal()) {
          position.x += childSize.width;
          maxCrossAxisChild = Math.max(maxCrossAxisChild, childSize.height);
        } else {
          position.y += childSize.height;
          maxCrossAxisChild = Math.max(maxCrossAxisChild, childSize.width);
        }
      });

      // Add final padding and edge gap after last object
      if (this.isHorizontal()) {
        position.x += layout.padding.right + layout.edgeGapAfter;
      } else {
        position.y += layout.padding.bottom + layout.edgeGapAfter;
      }

      return { position, maxCrossAxisChild };
    }

    // New helper method to position children
    private positionChildrenInLayout(
      children: fabric.Object[],
      layout: FrameLayoutInfo,
      updateAll?: boolean
    ) {
      const position: Position = {
        x:
          this.left +
          layout.padding.left +
          (this.isHorizontal() ? layout.edgeGapBefore : 0),
        y:
          this.top +
          layout.padding.top +
          (!this.isHorizontal() ? layout.edgeGapBefore : 0),
      };

      let addedChild = false;

      // Width/height of the largest child not in the direction of the layout.
      // In a horizontal layout, this would be the height of the tallest child.
      let maxCrossAxisChild = 0;

      // Position children within the layout based on layout statistics
      children.forEach((child, index) => {
        const childSize = {
          width: child.getScaledWidth(),
          height: child.getScaledHeight(),
        };

        if (addedChild) {
          // Use individual gap for this position (index-1 because we're adding gap before current child)
          const gapToUse = layout.individualGaps[index - 1] ?? layout.gap;
          if (this.isHorizontal()) {
            position.x += gapToUse;
          } else {
            position.y += gapToUse;
          }
        }

        // If the child is being dragged, then replace it with a transparent space
        // by not updating the child placeholder, but still incrementing the position.
        if (updateAll || !child.objectIsMoving()) {
          const { leftOffset, topOffset } = this.getChildOffset(layout, child);

          const newLeft = position.x + leftOffset;
          const newTop = position.y + topOffset;

          this.positionChild(child, newLeft, newTop);
        }

        addedChild = true;
        if (this.isHorizontal()) {
          position.x += childSize.width;
          maxCrossAxisChild = Math.max(maxCrossAxisChild, childSize.height);
        } else {
          position.y += childSize.height;
          maxCrossAxisChild = Math.max(maxCrossAxisChild, childSize.width);
        }
      });

      if (this.isHorizontal()) {
        position.x += layout.padding.right + layout.edgeGapAfter;
      } else {
        position.y += layout.padding.bottom + layout.edgeGapAfter;
      }

      return { maxCrossAxisChild, position };
    }

    private positionChild(
      child: fabric.Object,
      newLeft: number,
      newTop: number
    ) {
      if (newLeft === child.left && newTop === child.top) {
        return;
      }

      if (isSimpleGroup(child)) {
        // If the group is part of an activeSelection, remove the activeSelction
        // transform before laying out the group
        if (child._currentTransform) {
          child.resetTransform();
        } else {
          // On load, set the transform for the
          child.maybePrepareForTransform();
        }
        child.set({ left: newLeft, top: newTop });
        child.transformObjects('moving');
      } else {
        child.set({ left: newLeft, top: newTop });
        if (isFrameLike(child)) {
          child.fire('moving', { frameMoving: true });
          child.fire('moved');
        }
      }

      if (child.simpleGroup) {
        child.simpleGroup.syncFromObjects();
      }

      child.setCoords();
      child.synchronize(); // Keep connectors in sync.
    }

    private sizeToFitChildren(
      position: Position,
      maxCrossAxisChild: number,
      layout: FrameLayoutInfo
    ) {
      const newSize: SizeStruct = {};

      // mainAxis and crossAxis refer to the direction of the layout
      let mainAxisSize: number;
      let mainAxisScale: number;
      let crossAxisSize: number;
      let crossAxisScale: number;
      if (this.isHorizontal()) {
        mainAxisSize = position.x - this.left;
        if (this.layoutMinWidth) {
          mainAxisSize = Math.max(this.layoutMinWidth, mainAxisSize);
        }
        mainAxisScale = mainAxisSize / this.width;
        crossAxisSize =
          maxCrossAxisChild + layout.padding.top + layout.padding.bottom;
        if (this.layoutMinHeight) {
          crossAxisSize = Math.max(this.layoutMinHeight, crossAxisSize);
        }
        crossAxisScale = crossAxisSize / this.height;
      } else {
        mainAxisSize = position.y - this.top;
        if (this.layoutMinHeight) {
          mainAxisSize = Math.max(this.layoutMinHeight, mainAxisSize);
        }
        mainAxisScale = mainAxisSize / this.height;
        crossAxisSize =
          maxCrossAxisChild + layout.padding.left + layout.padding.right;
        if (this.layoutMinWidth) {
          crossAxisSize = Math.max(this.layoutMinWidth, crossAxisSize);
        }
        crossAxisScale = crossAxisSize / this.width;
      }

      const adjustWidth = this.resizeUsingWidth();
      const adjustHeight = this.resizeUsingHeight();

      // Only handle the 'children' case here - parent sizing is handled separately
      if (this.layoutSizeHorizontal === 'children') {
        if (adjustWidth) {
          newSize.width =
            (this.isHorizontal() ? mainAxisSize : crossAxisSize) / this.scaleX;
        } else {
          newSize.scaleX = this.isHorizontal() ? mainAxisScale : crossAxisScale;
        }
      }

      if (this.layoutSizeVertical === 'children') {
        if (adjustHeight) {
          newSize.height =
            (!this.isHorizontal() ? mainAxisSize : crossAxisSize) / this.scaleY;
        } else {
          newSize.scaleY = !this.isHorizontal()
            ? mainAxisScale
            : crossAxisScale;
        }
      }

      if (Object.keys(newSize).length > 0) {
        if (this.layoutMinWidth) {
          const effectiveWidth =
            (newSize.width ?? this.width) * (newSize.scaleX ?? this.scaleX);
          if (effectiveWidth < this.layoutMinWidth) {
            if (newSize.scaleX) {
              newSize.width = this.layoutMinWidth / newSize.scaleX;
            } else {
              newSize.width = this.layoutMinWidth / this.scaleX;
            }
          }
        }

        if (this.layoutMinHeight) {
          const effectiveHeight =
            (newSize.height ?? this.height) * (newSize.scaleY ?? this.scaleY);
          if (effectiveHeight < this.layoutMinHeight) {
            if (newSize.scaleY) {
              newSize.height = this.layoutMinHeight / newSize.scaleY;
            } else {
              newSize.height = this.layoutMinHeight / this.scaleY;
            }
          }
        }

        if (newSize.scaleX === 0) {
          delete newSize.scaleX;
        }
        if (newSize.scaleY === 0) {
          delete newSize.scaleY;
        }
        this.set(newSize);
        this.setCoords();
      }
    }

    // Force layout on all children of this object.
    layoutWithChildren() {
      this.layout();
      this.layoutChildren();
    }

    // Only layout the children of this object. Only called within internalLayout
    // after a layout is done
    private layoutChildren() {
      if (isFrameLike(this)) {
        this.childFrames.forEach(
          frame => hasLayout(frame) && frame.layoutWithChildren()
        );
      }
    }

    private getChildOffset(layout: FrameLayoutInfo, child: fabric.Object) {
      const { total, maxSize } = layout;
      const childSize = {
        width: child.getScaledWidth(),
        height: child.getScaledHeight(),
      };
      let leftOffset = 0;
      let topOffset = 0;
      let alignmentH = this.layoutAlignHorizontal;
      let alignmentV = this.layoutAlignVertical;

      if (!maxSize.width) alignmentH = 'left';
      if (!maxSize.height) alignmentV = 'top';

      switch (alignmentH) {
        case 'left':
          leftOffset = 0;
          break;
        case 'center':
          if (this.layoutDirection === 'horizontal') {
            leftOffset = maxSize.width! / 2 - total.width / 2;
          } else {
            leftOffset = maxSize.width! / 2 - childSize.width / 2;
          }
          break;
        case 'right':
          if (this.layoutDirection === 'horizontal') {
            leftOffset = maxSize.width! - total.width;
          } else {
            leftOffset = maxSize.width! - childSize.width;
          }
          break;
      }

      switch (alignmentV) {
        case 'top':
          topOffset = 0;
          break;
        case 'center':
          if (this.layoutDirection === 'horizontal') {
            topOffset = maxSize.height! / 2 - childSize.height / 2;
          } else {
            topOffset = maxSize.height! / 2 - total.height / 2;
          }
          break;
        case 'bottom':
          if (this.layoutDirection === 'horizontal') {
            topOffset = maxSize.height! - childSize.height;
          } else {
            topOffset = maxSize.height! - total.height;
          }
          break;
      }

      return { leftOffset, topOffset };
    }

    layoutPaddingSides(): SpacingSidesStruct {
      const padding = {
        left: this.layoutPadding || 0,
        top: this.layoutPadding || 0,
        right: this.layoutPadding || 0,
        bottom: this.layoutPadding || 0,
      };

      // For simplicity we incorporate the margin into the padding.
      let margin;
      if (this.layoutMarginSides) {
        margin = this.layoutMarginSides();
      } else {
        margin = { top: 0, left: 0, right: 0, bottom: 0 };
      }
      padding.left += margin.left;
      padding.top += margin.top;
      padding.right += margin.right;
      padding.bottom += margin.bottom;

      return padding;
    }

    cursorInProximity(): boolean {
      const pointer = this.controller.mousePosition;

      const offset = 20;
      const x = pointer.x;
      const y = pointer.y;
      const width = this.getScaledWidth();
      const height = this.getScaledHeight();

      return (
        x >= this.left - offset &&
        x <= this.left + width + offset &&
        y >= this.top - offset &&
        y <= this.top + height + offset
      );
    }

    cursorInProximityOfParent(): boolean {
      let parent: (FrameBehaviors & fabric.Object) | undefined = this;
      while ((parent = parent.parentFrame)) {
        if (hasLayout(parent) && parent.cursorInProximity()) {
          return true;
        }
      }
      return false;
    }

    findTopmostChildAtPoint(point: fabric.Point): fabric.Object | undefined {
      // Collect all descendants within this layout's hierarchy
      const allDescendants: fabric.Object[] = [];

      const collectDescendants = (objects: fabric.Object[]) => {
        for (const obj of objects) {
          allDescendants.push(obj);
          if (isFrameLike(obj) && obj.children) {
            collectDescendants(obj.children);
          }
        }
      };

      collectDescendants(this.children);

      // Find the topmost object at the point from our collected descendants
      // We iterate in reverse order since fabric renders later objects on top
      for (let i = allDescendants.length - 1; i >= 0; i--) {
        const obj = allDescendants[i];
        if (obj.containsPoint && obj.containsPoint(point)) {
          return obj;
        }
      }

      return undefined;
    }

    shouldRenderLayoutBorder() {
      return (
        !this.container ||
        // Always show border for nested layouts with no children
        (this.container && this.children.length === 0) ||
        // Show border when cursor nearby. Important for nested layouts that
        // otherwise do not show border
        !this.proximityBorder ||
        this.cursorInProximity() ||
        this.cursorInProximityOfParent()
      );
    }

    // Whether this object resizes using the width field rather than scaleX.
    resizeUsingWidth() {
      const resizeControl = this.controls.mr || this.controls.br;

      if (!resizeControl) {
        return false;
      }

      if (resizeControl instanceof ShapeLinearResizeControl) {
        return false;
      }

      return resizeControl instanceof ResizeControl;
    }

    // Whether this object resizes using the height field rather than scaleY.
    resizeUsingHeight() {
      const resizeControl = this.controls.mb || this.controls.br;

      if (!resizeControl) {
        return false;
      }

      if (resizeControl instanceof ShapeLinearResizeControl) {
        return false;
      }

      return resizeControl instanceof ResizeControl;
    }

    showParentFill() {
      const parent = this.inFrame() as FrameBehaviors &
        LayoutBehaviors &
        fabric.Object;
      if (parent) {
        return parent.layoutEnabled;
      }
      return false;
    }

    setIndicatorObject(object: FrameMemberBehaviors & fabric.Object) {
      this.indicatorObject = object;
    }

    clearIndicatorObject() {
      this.indicatorObject = null;
    }

    // When a user resize a layout while it is set to hug its contents, reset the
    // appropriate params to "user"
    private updateLayoutToManual(control) {
      if (control instanceof ShapeLinearResizeControl) {
        const edge = control.edge;
        if (['top', 'bottom'].includes(edge)) {
          this.set({ layoutSizeVertical: 'user' });
        } else {
          this.set({ layoutSizeHorizontal: 'user' });
        }
      } else {
        this.set({
          layoutSizeHorizontal: 'user',
          layoutSizeVertical: 'user',
        });
      }
    }

    private updateMinDimensions() {
      if (this.layoutEnabled) {
        this.minScaleLimit = 0;
        // At very small sizes the selection area of the controls overlaps, so
        // just show the corner controls for ease of use
        Object.entries(this.controls).forEach(([key, control]) => {
          if (['tl', 'tr', 'bl', 'br'].includes(key)) {
            (control as ShapeResizeControl).minDimension = 0;
          }
        });
      } else {
        this.minScaleLimit = 0.1;

        // Reset scaleX/Y to min if necessary
        let scaleX = this.scaleX;
        let scaleY = this.scaleY;
        if (this.scaleX < 0.1) scaleX = 0.1;
        if (this.scaleY < 0.1) scaleY = 0.1;
        this.set({ scaleX, scaleY });

        Object.entries(this.controls).forEach(([key, control]) => {
          if (['tl', 'tr', 'bl', 'br'].includes(key)) {
            (control as ShapeResizeControl).minDimension = 8;
          }
        });
      }
    }

    private updateAddControl() {
      this.controls.addObject ||= WithLayoutBehaviors.addControl;
      if (this.layoutEnabled) {
        const layoutDirection = this.layoutDirection;
        if (layoutDirection === 'wrap') {
          this.controls.addObject.visible = false;
        } else {
          this.controls.addObject.visible = true;
          (this.controls.addObject as AddObjectControl).updatePosition(
            layoutDirection
          );
        }
      } else {
        this.controls.addObject.visible = false;
      }
    }

    isHorizontal() {
      return this.layoutDirection === 'horizontal';
    }

    fillsParentHorizontally() {
      return this.layoutSizeHorizontal === 'parent';
    }

    fillsParentVertically() {
      return this.layoutSizeVertical === 'parent';
    }

    fillsParent() {
      return this.fillsParentHorizontally() || this.fillsParentVertically();
    }

    showAutoGapButton(): boolean {
      if (isFrameLike(this)) {
        return !this.isStylizedComponent();
      }

      return true;
    }

    private fitsToChildren() {
      return (
        this.layoutSizeHorizontal === 'children' ||
        this.layoutSizeVertical === 'children'
      );
    }

    // If size is set to children along the layout axis and auto gap is enabled,
    // set the sizing to user. So if the layout is vertical, and set to size to
    // children vertically, setting auto gap will change that sizing to user
    private updateAutoGap() {
      const layoutDirection = this.layoutDirection;
      if (this.layoutGap === 'auto') {
        if (layoutDirection === 'horizontal') {
          this.set('layoutSizeHorizontal', 'user');
        } else {
          this.set('layoutSizeVertical', 'user');
        }
      }
    }

    private updateParentSize() {
      const parent = this.inFrame();
      if (parent && hasLayout(parent)) {
        const { layoutSizeHorizontal, layoutSizeVertical } = this;
        const {
          layoutSizeHorizontal: parentSizeHorizontal,
          layoutSizeVertical: parentSizeVertical,
        } = parent;
        // Only update parent if there's a direct circular sizing dependency where
        // parent sizes to `children` and child sizes to `parent`. If this happens,
        // we get non-deterministic behavior, since neither layout will have a
        // defined size.
        if (
          layoutSizeHorizontal === 'parent' &&
          parentSizeHorizontal === 'children'
        ) {
          console.debug('setting parent horizontal');
          parent.set('layoutSizeHorizontal', 'user');
        }
        if (
          layoutSizeVertical === 'parent' &&
          parentSizeVertical === 'children'
        ) {
          console.debug('setting parent vertical');
          parent.set('layoutSizeVertical', 'user');
        }
      }
    }

    private updateChildrenSize() {
      const children = this.childFrames;
      const { layoutSizeHorizontal, layoutSizeVertical } = this;
      children.forEach(child => {
        if (hasLayout(child)) {
          const {
            layoutSizeHorizontal: childSizeHorizontal,
            layoutSizeVertical: childSizeVertical,
          } = child;
          // Only update parent if there's a direct circular sizing dependency where
          // parent sizes to `children` and child sizes to `parent`. If this happens,
          // we get non-deterministic behavior, since neither layout will have a
          // defined size.
          if (
            layoutSizeHorizontal === 'children' &&
            childSizeHorizontal === 'parent'
          ) {
            child.set('layoutSizeHorizontal', 'user');
          }
          if (
            layoutSizeVertical === 'children' &&
            childSizeVertical === 'parent'
          ) {
            child.set('layoutSizeVertical', 'user');
          }
        }
      });
    }

    // If this frame is sizing to fit the parent, we need to call layout on the parent,
    // which will update this frame's size. Note: we probably don't need to check
    // prevValue here, but this guarantees we won't needelessly rerun layout
    private handleLayoutSizeChange(prevValue: any, newValue: any) {
      // If this frame is sizing to fit the parent, we need to call layout on the parent,
      // which will update this frame's size
      if (prevValue !== 'parent' && newValue === 'parent') {
        const parent = this.inFrame();
        if (!parent || !hasLayout(parent)) {
          // This should not happen because a layout should not be able to set its
          // sizing to fill parent without being in a parent.
          console.error('No parent when fill parent is set');
          return;
        }

        parent.layout();
        return;
      }

      this.layout();
    }
  };

  return WithLayoutBehaviors;
}

export default LayoutBehaviors;
